import { DateTime, Duration } from "luxon";
import type { FC } from "react";
import React from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";

import { hasValue } from "@core/foundation/types";
import { MemSize } from "@core/foundation/units";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@react/ui-primitives/chart";

import type { Metric, MetricUnit } from "@apis/admin/api";

const countFormatter = new Intl.NumberFormat("en", {
  maximumFractionDigits: 1,
  notation: "compact",
});

const decimalFormatter = new Intl.NumberFormat("en", {
  maximumFractionDigits: 1,
  useGrouping: false,
});

function formatTick(timestamp: string): string {
  return DateTime.fromISO(timestamp).toFormat("HH:mm");
}

function formatValue(value: number, unit: MetricUnit): string {
  if (value === 0) {
    return "0";
  }

  switch (unit) {
    case "bytes": {
      const size = MemSize.bytes(value);

      if (size.toGigabytes() >= 1) {
        return `${decimalFormatter.format(size.toGigabytes())} GB`;
      }

      if (size.toMegabytes() >= 1) {
        return `${decimalFormatter.format(size.toMegabytes())} MB`;
      }

      if (size.toKilobytes() >= 1) {
        return `${decimalFormatter.format(size.toKilobytes())} KB`;
      }

      return `${value} B`;
    }
    case "count":
      return countFormatter.format(value);
    case "milliseconds": {
      const duration = Duration.fromMillis(value);

      if (duration.as("minutes") >= 1) {
        return `${decimalFormatter.format(duration.as("minutes"))} min`;
      }

      if (duration.as("seconds") >= 1) {
        return `${decimalFormatter.format(duration.as("seconds"))} s`;
      }

      return `${value} ms`;
    }
    case "minutes": {
      const duration = Duration.fromObject({ minutes: value });

      if (duration.as("minutes") >= 1) {
        return `${decimalFormatter.format(duration.as("minutes"))} min`;
      }

      if (duration.as("seconds") >= 1) {
        return `${decimalFormatter.format(duration.as("seconds"))} s`;
      }

      return `${value} ms`;
    }
  }
}

function normalizeMetric(metric: Metric): Metric {
  switch (metric.unit) {
    case "bytes":
    case "count":
    case "minutes":
      return metric;
    case "milliseconds":
      return {
        alertThreshold: hasValue(metric.alertThreshold)
          ? Duration.fromMillis(metric.alertThreshold).as("minutes")
          : undefined,
        datapoints: metric.datapoints.map(point => {
          return {
            timestamp: point.timestamp,
            value: Duration.fromMillis(point.value).as("minutes"),
          };
        }),
        id: metric.id,
        label: metric.label,
        shutdownThreshold: hasValue(metric.shutdownThreshold)
          ? Duration.fromMillis(metric.shutdownThreshold).as("minutes")
          : undefined,
        unit: "minutes",
      };
  }
}

interface MetricChartProps {
  readonly metric: Metric;
}

export const MetricChart: FC<MetricChartProps> = ({
  metric: originalMetric,
}) => {
  const { alertThreshold, datapoints, label, shutdownThreshold, unit } =
    normalizeMetric(originalMetric);

  const Y_AXIS_HEADROOM = 1.1;
  const yMax =
    Y_AXIS_HEADROOM *
    Math.max(
      ...datapoints.map(point => point.value),
      alertThreshold ?? 0,
      shutdownThreshold ?? 0,
    );

  return (
    <ChartContainer
      className="h-64 w-full"
      config={{ value: { color: "var(--chart-1)", label: "Value" } }}
    >
      <LineChart
        data={[...datapoints]}
        margin={{ bottom: 8, left: 8, right: 8, top: 8 }}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="timestamp"
          minTickGap={32}
          tickFormatter={formatTick}
          tickLine={false}
        />
        <YAxis
          domain={[0, yMax]}
          scale="sqrt"
          tickFormatter={value => formatValue(Number(value), unit)}
          tickLine={false}
          width={72}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(value: unknown) =>
                `${formatValue(Number(value), unit)} — ${label}`
              }
              labelFormatter={(value: unknown) =>
                DateTime.fromISO(String(value))
                  .setLocale("en-US")
                  .toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
              }
            />
          }
        />
        <Line
          dataKey="value"
          dot={false}
          stroke="var(--color-value)"
          strokeWidth={2}
          type="monotone"
        />
        {hasValue(alertThreshold) && (
          <ReferenceLine
            label={{
              fill: "var(--warning)",
              position: "insideTopRight",
              value: "Alert",
            }}
            stroke="var(--warning)"
            strokeDasharray="4 4"
            y={alertThreshold}
          />
        )}
        {hasValue(shutdownThreshold) && (
          <ReferenceLine
            label={{
              fill: "var(--destructive)",
              position: "insideTopRight",
              value: "Shutdown",
            }}
            stroke="var(--destructive)"
            strokeDasharray="4 4"
            y={shutdownThreshold}
          />
        )}
      </LineChart>
    </ChartContainer>
  );
};
